import { useState, useEffect } from 'react';
import { getAnalyticsSummary, getAnalyticsEvents, getAdminCampaigns } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Eye, ShoppingBag, TrendingUp, MousePointerClick, Copy, ExternalLink, Mail, UserPlus, ShoppingCart } from 'lucide-react';
import axios from 'axios';

const mlApi = axios.create({ baseURL: `${window.location.origin}/api`, withCredentials: true });

const statIcons = {
  total_users: Users, page_views: Eye, purchases_this_month: ShoppingBag,
  conversion_rate: TrendingUp, product_clicks: MousePointerClick,
  promo_copies: Copy, redirects: ExternalLink, events_today: Eye,
};

const statLabels = {
  total_users: 'Utenti Totali', page_views: 'Visite (mese)', purchases_this_month: 'Acquisti (mese)',
  conversion_rate: 'Tasso Conversione', product_clicks: 'Click Prodotti', buy_now_clicks: 'Click Acquista',
  promo_copies: 'Codici Copiati', redirects: 'Redirect Sito', events_today: 'Eventi Oggi',
};

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [mlStatus, setMlStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, e, c] = await Promise.all([
          getAnalyticsSummary(), getAnalyticsEvents({ limit: 15 }), getAdminCampaigns()
        ]);
        setSummary(s.data);
        setEvents(e.data);
        setCampaigns(c.data);
        try {
          const ml = await mlApi.get('/admin/mailerlite/status');
          setMlStatus(ml.data);
        } catch {}
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} /></div>;
  }

  const mainStats = ['total_users', 'events_today', 'purchases_this_month', 'conversion_rate'];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', fontWeight: 600 }}>Dashboard</h1>
        <p className="font-body text-sm" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>Panoramica del microsite</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summary && mainStats.map(key => {
          const Icon = statIcons[key] || Eye;
          const val = key === 'conversion_rate' ? `${summary[key]}%` : summary[key];
          return (
            <Card key={key} className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid={`stat-${key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Icon className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                  <span className="font-mono text-2xl font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{val}</span>
                </div>
                <p className="font-body text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>{statLabels[key]}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Campaigns Status + Recent Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Campaigns */}
        <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="campaigns-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Campagne del Mese</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {campaigns.slice(0, 4).map(c => (
                <div key={c.campaign_id} className="flex items-center gap-3 py-2 border-b last:border-b-0" style={{ borderColor: 'var(--border-light)' }}>
                  <img src={c.image_url} alt={c.title} className="w-10 h-14 object-cover rounded-sm" style={{ border: '1px solid var(--border-light)' }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{c.title}</p>
                    <p className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>{c.promo_code || 'Nessun codice'}</p>
                  </div>
                  <Badge className="text-xs rounded-sm" style={{
                    backgroundColor: c.is_currently_active ? 'rgba(59,82,67,0.1)' : 'rgba(138,141,138,0.1)',
                    color: c.is_currently_active ? 'var(--accent-success)' : 'var(--text-muted)'
                  }}>
                    {c.is_currently_active ? 'Attivo' : 'Spento'}
                  </Badge>
                </div>
              ))}
              {campaigns.length === 0 && (
                <p className="font-body text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>Nessuna campagna trovata</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Events */}
        <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="recent-events-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Eventi Recenti</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-auto">
              {events.map((e, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-b-0" style={{ borderColor: 'var(--border-light)' }}>
                  <div>
                    <Badge variant="outline" className="text-xs rounded-sm font-mono mr-2" style={{ fontFamily: 'var(--font-mono)' }}>
                      {e.event_type}
                    </Badge>
                    <span className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
                      {e.user_id ? e.user_id.substring(0, 12) + '...' : 'Anonimo'}
                    </span>
                  </div>
                  <span className="font-mono text-xs" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    {e.created_at ? new Date(e.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              ))}
              {events.length === 0 && (
                <p className="font-body text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>Nessun evento registrato</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {['product_clicks', 'buy_now_clicks', 'promo_copies', 'redirects'].map(key => (
            <Card key={key} className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
              <CardContent className="p-4 text-center">
                <p className="font-mono text-xl" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{summary[key]}</p>
                <p className="font-body text-xs mt-1" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>{statLabels[key]}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* MailerLite Status */}
      {mlStatus && mlStatus.configured && (
        <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="mailerlite-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>
              <Mail className="w-4 h-4" style={{ color: 'var(--accent)' }} />
              MailerLite
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {Object.entries(mlStatus.groups || {}).map(([name, data]) => {
                const icons = { 'Quintessenza Microsite': UserPlus, 'Acquisti Quintessenza': ShoppingBag, 'Carrello Abbandonato': ShoppingCart };
                const Icon = icons[name] || Users;
                return (
                  <div key={name} className="flex items-center gap-3 p-3 rounded-sm" style={{ backgroundColor: 'var(--bg-warm)' }}>
                    <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}>
                      <Icon className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                    </div>
                    <div>
                      <p className="text-xl font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{data.subscriber_count}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{name}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
